import { io } from 'socket.io-client';
import { useEffect } from 'react';
import { AuthStorage, useAuth, useClient, useQueryClient } from 'shared-ui';

import { WebsocketContext } from '../websocketContext/WebsocketContext';
import { QueryKeyEnum } from 'core/global.enum';

import { WebsocketContextControllerProps } from './WebsocketContextController.types';

const isProduction = process.env.NODE_ENV === 'production';
// Development defaults to the ingress proxy a bare `npm start` sits behind (localhost/api/prod).
// Against a port-forwarded backend (E2E stack, `REACT_APP_API_URL=http://localhost:13000`) that
// proxy does not exist, the socket never connects and every page shows "connection lost", so the
// two overrides let a dev point the socket at the same backend as the API.
const baseUrl = isProduction ? process.env.REACT_APP_API_URL || '' : process.env.REACT_APP_WS_URL || 'http://localhost';
const path = isProduction ? '/api/socket.io' : process.env.REACT_APP_WS_PATH || '/api/prod/socket.io';

const socket = io(baseUrl, {
  path,
  transports: ['websocket'],
  autoConnect: false,
});

export const WebsocketContextController = ({
  children,
  storageType = 'sessionStorage',
}: WebsocketContextControllerProps) => {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const authStorage = new AuthStorage(storageType);
  const { setHasPermissionChanged } = useClient();

  useEffect(() => {
    if (isAuthenticated) {
      socket.auth = { token: `Bearer ${authStorage.accessToken}` };
      socket.disconnect().connect();

      socket.on('logged.users.changed', () => {
        queryClient.invalidateQueries([QueryKeyEnum.USERS]);
      });

      socket.on('logged.user.permissions.changed', () => {
        setHasPermissionChanged(true);
      });
    }
  }, [isAuthenticated]);

  return <WebsocketContext.Provider value={socket}>{children}</WebsocketContext.Provider>;
};
